import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { showToaster } from "../../../common/common";
import { useLocation, useNavigate } from "react-router-dom";
import PostRequest from "../../../services/PostRequest";

const OtpVerify = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(30);
  const otpInputsRef = useRef([]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Redirect to sign-up if no location.state
  useEffect(() => {
    if (!location.state) {
      window.location.href = "/sign-up";
    }
  }, [location.state]);

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9!@#$%^&*]/.test(password)) strength++;
    return Math.min(strength, 4);
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow numeric input
    if (!/^\d*$/.test(value)) return;
    if (value.length > 1) value = value[0];

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-advance to next input
    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  // Handle OTP keydown for backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // Move to previous input on backspace if current is empty
        otpInputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  // Handle paste into OTP field
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    const pasteDigits = paste.replace(/\D/g, "").slice(0, 6).split("");
    
    if (pasteDigits.length > 0) {
      const newOtp = [...otp];
      pasteDigits.forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);
      
      // Focus last filled input or first empty input
      const lastIndex = Math.min(pasteDigits.length - 1, 5);
      setTimeout(() => {
        otpInputsRef.current[lastIndex]?.focus();
      }, 0);
    }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.password) {
      errors.password = "Password Required";
    } else if (values.password.length < 8) {
      errors.password = "Password must be at least 8 characters long.";
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = "Confirm Password Required";
    } else if (values.confirmPassword !== values.password) {
      errors.confirmPassword = "Passwords must match";
    }
    return errors;
  };

  const handleOtpVerify = async (data) => {
    try {
      const otpString = otp.join("");
      if (otpString.length !== 6) {
        showToaster("Please enter a valid 6-digit OTP", "error");
        return false;
      }

      const password = data.password;
      const payload = { ...location?.state, otp: otpString };
      const newPayload = { ...payload, password };

      const option = {
        showLoader: "verify-id",
        auth: false,
      };

      const response = await PostRequest("/users/register", newPayload, option);
      showToaster("Your account has been created successfully!");
      navigate("/sign-in");
    } catch (error) {
      console.log(error.message);
      showToaster(error.message, "error");
    }
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate,
    onSubmit: handleOtpVerify,
  });

  // Extract email from location.state for display
  const userEmail = location?.state?.email || "example@company.com";
  const maskedEmail = userEmail.replace(/(.)(.*?)(@)/g, "$1•••$3");

  if (!location.state) {
    return null;
  }

  return (
    <div className="auth">
      <div className="auth__main">
        <div className="auth__card">
          <img
            className="auth__logo auth__logo--color"
            src="/assets/brand/multicartapi-logo.png"
            alt="MultiCartAPI"
          />
          <img
            className="auth__logo auth__logo--white"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />
          <h1>Complete your account</h1>
          <p className="auth__sub">
            We sent a 6-digit code to <strong>{maskedEmail}</strong>. Enter it
            below and set a password.
          </p>

          <form className="auth__form" onSubmit={formik.handleSubmit}>
            {/* OTP Field */}
            <div className="mca-field">
              <label className="mca-label">
                Verification code <span className="req">*</span>
              </label>
              <div className="mca-otp" id="otp">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputsRef.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    aria-label={`Digit ${index + 1}`}
                    className={digit ? "is-filled" : ""}
                  />
                ))}
              </div>
              <div className="mca-help" id="resend" style={{ marginTop: "10px" }}>
                Didn't get it? Resend code in{" "}
                <strong id="cd">
                  0:{resendCountdown < 10 ? "0" : ""}
                  {resendCountdown}
                </strong>
              </div>
            </div>

            {/* Password Field */}
            <div className="mca-field">
              <label className="mca-label" htmlFor="ov-pw">
                Password <span className="req">*</span>
              </label>
              <div className="pw-field">
                <input
                  className="mca-input"
                  id="ov-pw"
                  type={passwordVisible ? "text" : "password"}
                  placeholder="At least 8 characters"
                  name="password"
                  value={formik.values.password}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setPasswordStrength(
                      calculatePasswordStrength(e.target.value)
                    );
                  }}
                  onBlur={formik.handleBlur}
                  required
                />
                <button
                  type="button"
                  className={`mca-iconbtn pw-field__toggle ${
                    passwordVisible ? "is-revealed" : ""
                  }`}
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  aria-label="Show password"
                >
                  <svg
                    className="ico-on"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="2.6" />
                  </svg>
                  <svg
                    className="ico-off"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 3l18 18" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                    <path d="M9.4 5.2A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.2 6.2A17 17 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3-.5" />
                  </svg>
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "8px",
                }}
              >
                <div
                  className="mca-strength"
                  id="ovStrength"
                  data-level={passwordStrength}
                  style={{ flex: 1 }}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span
                  className="mca-help"
                  data-strength-label
                  style={{ minWidth: "42px" }}
                >
                  {passwordStrength === 0 && "None"}
                  {passwordStrength === 1 && "Weak"}
                  {passwordStrength === 2 && "Fair"}
                  {passwordStrength === 3 && "Good"}
                  {passwordStrength === 4 && "Strong"}
                </span>
              </div>
              {formik.touched.password && formik.errors.password && (
                <div className="mca-error">
                  {formik.errors.password}
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="mca-field">
              <label className="mca-label" htmlFor="ov-confirm">
                Confirm password <span className="req">*</span>
              </label>
              <div className="pw-field">
                <input
                  className="mca-input"
                  id="ov-confirm"
                  type={confirmVisible ? "text" : "password"}
                  placeholder="Re-enter password"
                  name="confirmPassword"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                <button
                  type="button"
                  className={`mca-iconbtn pw-field__toggle ${
                    confirmVisible ? "is-revealed" : ""
                  }`}
                  onClick={() => setConfirmVisible(!confirmVisible)}
                  aria-label="Show password"
                >
                  <svg
                    className="ico-on"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="2.6" />
                  </svg>
                  <svg
                    className="ico-off"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 3l18 18" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                    <path d="M9.4 5.2A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.2 6.2A17 17 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3-.5" />
                  </svg>
                </button>
              </div>
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <div className="mca-error">
                  {formik.errors.confirmPassword}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              className="mca-btn mca-btn--primary"
              id="ov-btn"
              type="submit"
              style={{ width: "100%", marginTop: "4px" }}
              disabled={formik.isSubmitting}
            >
              <span
                className="mca-btn__spin"
                style={{ display: formik.isSubmitting ? "inline-flex" : "none" }}
              >
                <span className="mca-spinner"></span>
              </span>
              <span className="mca-btn__label">
                {formik.isSubmitting ? "Creating…" : "Create account"}
              </span>
            </button>
          </form>

          <a className="auth__back" href="/sign-in">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 6-6 6 6 6" />
            </svg>
            Back to sign in
          </a>
        </div>
      </div>

      {/* Brand Panel */}
      <div className="auth__brand">
        <div>
          <img
            className="auth__brand-logo"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />
          <h2>Almost there.</h2>
          <p>
            Verifying your email keeps your account and API key secure.
          </p>
        </div>
        <ul className="auth__feat">
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            Secure email verification
          </li>
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            Your API key is private to you
          </li>
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            Revoke &amp; rotate anytime
          </li>
        </ul>
        <div className="auth__brand-foot">
          Copyright © {new Date().getFullYear()} MultiCartAPI. All rights
          reserved.
        </div>
      </div>

      <div data-toast-host></div>
    </div>
  );
};

export default OtpVerify;
